import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, CheckCircle2, Loader, Play, X, XCircle, AlertTriangle } from 'lucide-react';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { scheduledCommandsApi } from '../../services/scheduledCommandsApi';
import type { RunResult, Schedule } from '../../types/scheduledCommands';
import { scheduleDisplayName } from '../../utils/scheduledCommands';
import { cn } from '../../utils/cn';

interface RunNowModalProps {
  schedule: Schedule;
  /** How many servers the targets reach, when the server list is loaded. */
  reachCount: number | null;
  onClose: () => void;
  /** Called after a run, so the list can show the new last run. */
  onRan: () => void;
}

/**
 * Run a schedule once, now, outside its timer.
 *
 * It goes to every targeted server, the same as a scheduled run, so the modal
 * asks first and then reports each server that did not run it. The timer is
 * not moved: the next scheduled run still happens when it was going to.
 */
export const RunNowModal = ({ schedule, reachCount, onClose, onRan }: RunNowModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [phase, setPhase] = useState<'confirm' | 'running' | 'done'>('confirm');
  const [result, setResult] = useState<RunResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const close = () => {
    if (phase !== 'running') onClose();
  };
  useOnClickOutside(modalRef, close);

  const run = async () => {
    setPhase('running');
    setError(null);
    try {
      const outcome = await scheduledCommandsApi.run(schedule.id);
      setResult(outcome);
      setPhase('done');
      onRan();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The command could not be run.');
      setPhase('confirm');
    }
  };

  const name = scheduleDisplayName(schedule);
  const where = reachCount === null ? 'on its targets' : `on ${reachCount} server${reachCount === 1 ? '' : 's'}`;
  const status = result?.run.status;

  return createPortal(
    <div className="modal-overlay z-[9999]">
      <div ref={modalRef} className="modal-panel max-w-lg" role="dialog" aria-modal="true" aria-labelledby="run-now-title">
        <div className="modal-header">
          <div className="min-w-0">
            <h2 id="run-now-title" className="modal-title truncate">Run “{name}” now</h2>
            <p className="modal-subtitle">Runs once {where}. The regular schedule carries on as before.</p>
          </div>
          <button type="button" onClick={close} className="modal-close" title="Close" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="modal-body space-y-3">
          {phase !== 'done' && (
            <pre className="subcard p-3 max-h-40 overflow-auto font-mono text-xs text-glass-300 whitespace-pre-wrap break-all">
              {schedule.command}
            </pre>
          )}

          {phase === 'running' && (
            <p className="text-xs text-glass-400" role="status">
              Waiting for the servers to answer. Silent ones are retried over RCON, so this can take up to half a minute.
            </p>
          )}

          {error && (
            <div className="alert alert-danger" role="alert">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {phase === 'done' && result && (
            <>
              <div
                className={cn('alert', status === 'ok' ? 'alert-success' : status === 'partial' ? 'alert-warning' : 'alert-danger')}
                role="status"
              >
                {status === 'ok' ? (
                  <CheckCircle2 className="w-4 h-4 shrink-0" />
                ) : status === 'partial' ? (
                  <AlertTriangle className="w-4 h-4 shrink-0" />
                ) : (
                  <XCircle className="w-4 h-4 shrink-0" />
                )}
                <span>
                  {status === 'ok' && `Ran on all ${result.run.responded_count} server${result.run.responded_count === 1 ? '' : 's'}.`}
                  {status === 'partial' && `Ran on ${result.run.responded_count} of ${result.run.expected_count} servers.`}
                  {status === 'failed' && (result.run.error ?? 'No server ran it.')}
                  {status === 'error' && `The run broke before it finished${result.run.error ? `: ${result.run.error}` : '.'}`}
                  {' '}It is in the run history.
                </span>
              </div>
              {result.failed.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-glass-400 mb-1.5">
                    Did not run it, even through the RCON fallback
                  </p>
                  <ul className="subcard p-3 max-h-48 overflow-auto space-y-1">
                    {result.failed.map((server) => (
                      <li key={server} className="font-mono text-xs text-glass-300">{server}</li>
                    ))}
                  </ul>
                  <p className="text-xs text-glass-500 mt-1.5">Usually these are offline or restarting. The status monitor shows which.</p>
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-footer">
          {phase === 'done' ? (
            <button type="button" onClick={onClose} className="btn-primary">Done</button>
          ) : (
            <>
              <button type="button" onClick={close} className="glass-button-secondary" disabled={phase === 'running'}>
                Cancel
              </button>
              <button type="button" onClick={() => void run()} className="btn-primary" disabled={phase === 'running'}>
                {phase === 'running' ? <Loader className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                {phase === 'running' ? 'Running…' : 'Run now'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};
